import React, { useState, useEffect } from 'react';
import './PopupModal.css';
import AllSBProductionMaterialsDailyReport, { countReportIssues } from './AllSBProductionMaterialsDailyReport';
import { isOff } from '../utils/inventoryConsumptionReport';
import AllSetsProductionMaterialsDailyReport from './AllSetsProductionMaterialsDailyReport';
import AllMWProductionMaterialsDailyReport from './AllMWProductionMaterialsDailyReport';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt } from '@fortawesome/free-solid-svg-icons';
import api from '../api/client';

const lb = (n) => Number(n || 0).toFixed(2);

function PopupModal({ isOpen, onClose, date, endpoint, columns, hint }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSKUs, setExpandedSKUs] = useState(new Set());
  const [summary, setSummary] = useState({ totalPlanned: 0, totalProduced: 0, planCompletion: 0 });
  const [allSBOpen, setAllSBOpen] = useState(false);
  const [allSetsOpen, setAllSetsOpen] = useState(false);
  const [allMWOpen, setAllMWOpen] = useState(false);

  // New states for report data and availability
  const [sbReportData, setSbReportData] = useState([]);
  const [setsReportData, setSetsReportData] = useState({ masterBags: [], smallPackets: [] });
  const [mwReportData, setMwReportData] = useState({ masterBags: [], seeds: [] });
  const [canOpenSB, setCanOpenSB] = useState(false);
  const [canOpenSets, setCanOpenSets] = useState(false);
  const [canOpenMW, setCanOpenMW] = useState(false);
  // New states for noCount
  const [sbNoCount, setSbNoCount] = useState(0);
  const [setsNoCount, setSetsNoCount] = useState(0);
  const [mwNoCount, setMwNoCount] = useState(0);

  useEffect(() => {
    if (isOpen && date && endpoint) {
      const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
          const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
          const url = `${endpoint}?prodDate=${formattedDate}`;
          console.log('Fetching popup data from:', url); // Debug log
          const response = await api.get(url).catch(err => {
            if (err.response) throw new Error(`HTTP error! Status: ${err.response.status}`);
            throw err;
          });
          const result = response.data;
          const fetchedData = Array.isArray(result) ? result : [];
          console.log('Received popup data:', fetchedData); // Debug log
          setData(fetchedData);

          // Calculate summary data
          const totalPlanned = fetchedData.reduce((sum, item) => sum + (Number(item.planned_qty) || 0), 0);
          const totalProduced = fetchedData.reduce((sum, item) => sum + (Number(item.total_qty) || 0), 0);
          const planCompletion = totalPlanned > 0
            ? (totalProduced / totalPlanned) * 100
            : (totalProduced === 0 ? 100 : 0);
          setSummary({
            totalPlanned,
            totalProduced,
            planCompletion: planCompletion.toFixed(2)
          });

          // Pre-fetch report data based on endpoint type
          if (fetchedData.length > 0) {
            const isSB = endpoint.includes('sbproduction_calendar_details');
            const isSetsEndpoint = endpoint.includes('sets_calendar_details');
            const isMWEndpoint = endpoint.includes('microgreens_wildflowers_calendar_details');

            if (isSB) {
              // Fetch SB report data
              const fetchSBReportData = async () => {
                try {
                  const fetchPromises = fetchedData
                    .filter(product => product.recID)
                    .map(async (product) => {
                      const recIDString = String(product.recID);
                      const reportUrl = `/api/materials_report?prodDate=${formattedDate}&recID=${encodeURIComponent(recIDString)}`;
                      console.log('Pre-fetching SB materials report for', product.sku, 'from:', reportUrl);
                      const controller = new AbortController();
                      const timeoutId = setTimeout(() => controller.abort(), 10000);
                      try {
                        const response = await api.get(reportUrl, { signal: controller.signal });
                        clearTimeout(timeoutId);
                        return { sku: product.sku, name: product.packName, data: response.data };
                      } catch (err) {
                        const reportErr = err.response
                          ? new Error(`HTTP error for ${product.sku}! Status: ${err.response.status}, Message: ${(err.response.data && err.response.data.error) || 'Unknown'}`)
                          : err;
                        console.error(`Error pre-fetching report for ${product.sku}:`, reportErr);
                        return { sku: product.sku, name: product.packName, error: reportErr.message };
                      }
                    });
                  const results = await Promise.all(fetchPromises);
                  console.log('Pre-fetched SB materials reports:', results);
                  setSbReportData(results);

                  // Failed stock checks + operations off the plan
                  setSbNoCount(countReportIssues(results));

                  // Check if any product has before > 0
                  const hasBefore = results.some(product => {
                    if (product.error) return false;
                    const seeds = product.data?.seeds || {};
                    const smallBags = product.data?.smallbags || {};
                    const seedsBefore = Number(seeds.qtyBefore ?? 0);
                    const sbBefore = Number(smallBags.qtyBefore ?? 0);
                    return seedsBefore > 0 || sbBefore > 0;
                  });
                  setCanOpenSB(hasBefore);
                } catch (err) {
                  console.error('Error pre-fetching SB reports:', err);
                  setSbReportData([]);
                  setCanOpenSB(false);
                  setSbNoCount(0);
                }
              };
              fetchSBReportData();
            } else if (isSetsEndpoint) {
              // Fetch Sets report data
              const fetchSetsReportData = async () => {
                try {
                  const reportUrl = `/api/sets_materials_wright_off?prodDate=${formattedDate}`;
                  console.log('Pre-fetching Sets materials report from:', reportUrl);
                  const response = await api.get(reportUrl).catch(err => {
                    if (err.response) throw new Error(`HTTP error! Status: ${err.response.status}`);
                    throw err;
                  });
                  const result = response.data;
                  setSetsReportData(result || { masterBags: [], smallPackets: [] });

                  // Calculate noCount for Sets
                  const noOccurrences = [
                    ...(result.masterBags || []),
                    ...(result.smallPackets || [])
                  ].reduce((count, row) => Number(row.now ?? 0) === Number(row.shouldBe ?? 0) ? count : count + 1, 0);
                  setSetsNoCount(noOccurrences);

                  // Check if any before > 0
                  const hasBefore = (result.masterBags || []).some(row => Number(row.before ?? 0) > 0) ||
                                    (result.smallPackets || []).some(row => Number(row.before ?? 0) > 0);
                  setCanOpenSets(hasBefore);
                } catch (err) {
                  console.error('Error pre-fetching Sets materials report:', err);
                  setSetsReportData({ masterBags: [], smallPackets: [] });
                  setCanOpenSets(false);
                  setSetsNoCount(0);
                }
              };
              fetchSetsReportData();
            } else if (isMWEndpoint) {
              // Fetch MW report data
              const fetchMWReportData = async () => {
                try {
                  const reportUrl = `/api/mw_materials_wright_off?prodDate=${formattedDate}`;
                  console.log('Pre-fetching MW materials report from:', reportUrl);
                  const response = await api.get(reportUrl).catch(err => {
                    if (err.response) throw new Error(`HTTP error! Status: ${err.response.status}`);
                    throw err;
                  });
                  const result = response.data;
                  setMwReportData(result || { masterBags: [], seeds: [] });

                  // Calculate noCount for MW
                  const noOccurrences = [
                    ...(result.masterBags || []),
                    ...(result.seeds || [])
                  ].reduce((count, row) => Number(row.now ?? 0) === Number(row.shouldBe ?? 0) ? count : count + 1, 0);
                  setMwNoCount(noOccurrences);

                  // Check if any before > 0
                  const hasBefore = (result.masterBags || []).some(row => Number(row.before ?? 0) > 0) ||
                                    (result.seeds || []).some(row => Number(row.before ?? 0) > 0);
                  setCanOpenMW(hasBefore);
                } catch (err) {
                  console.error('Error pre-fetching MW materials report:', err);
                  setMwReportData({ masterBags: [], seeds: [] });
                  setCanOpenMW(false);
                  setMwNoCount(0);
                }
              };
              fetchMWReportData();
            }
          }
        } catch (err) {
          console.error('Error fetching popup data:', err);
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [isOpen, date, endpoint]);

  const toggleExpand = (sku) => {
    setExpandedSKUs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sku)) {
        newSet.delete(sku);
      } else {
        newSet.add(sku);
      }
      return newSet;
    });
  };

  const openAllSBReport = () => {
    if (canOpenSB) {
      setAllSBOpen(true);
    }
  };

  const openAllSetsReport = () => {
    if (canOpenSets) {
      setAllSetsOpen(true);
    }
  };

  const openAllMWReport = () => {
    if (canOpenMW) {
      setAllMWOpen(true);
    }
  };

  if (!isOpen) return null;

  const isHierarchical = data.length > 0 && data[0].details;
  const isSets = endpoint.includes('sets_calendar_details');
  const isMW = endpoint.includes('microgreens_wildflowers_calendar_details');
  const isSB = endpoint.includes('sbproduction_calendar_details');
  const showSummary = !isSB;
  // Fertilizers send the raw material needed and used per SKU
  const hasMaterial = isHierarchical && data[0].needed_lb !== undefined;

  const mainColumns = [
    { label: '', key: 'expand' },
    { label: 'SKU', key: 'sku' },
    { label: 'Name', key: 'name' },
    { label: 'Employees', key: 'employees' },
    { label: 'Boxes', key: 'box_count' },
    { label: 'Produced', key: 'total_qty' },
    { label: 'Planned', key: 'planned_qty' },
    { label: 'Plan %', key: 'plan_completion', format: (value) => value.toFixed(2) + '%' },
    ...(hasMaterial ? [
      { label: 'Material Needed (lb)', key: 'needed_lb' },
      { label: 'Material Used (lb)', key: 'used_lb' },
      { label: 'Diff (lb)', key: 'material_diff' },
    ] : []),
  ];

  const detailColumns = [
    { label: 'Employee Name', key: 'employeeName' },
    { label: 'Boxes Made', key: 'box_count' },
    { label: 'Sets Made', key: 'total_qty' },
    { label: 'Individual Plan', key: 'individual_plan', format: (value) => value.toFixed(2) },
    { label: 'Percentage Completed', key: 'percentage_completed', format: (value) => value.toFixed(2) + '%' },
  ];

  const getReportButton = (type) => {
    const isDisabled = type === 'SB' ? !canOpenSB : type === 'Sets' ? !canOpenSets : !canOpenMW;
    const noCount = type === 'SB' ? sbNoCount : type === 'Sets' ? setsNoCount : mwNoCount;
    const title = isDisabled 
      ? "No report available due to missing saved data." 
      : type === 'SB' 
        ? "View All Materials Report" 
        : type === 'Sets' 
          ? "View Materials Wright Off Report (sets)" 
          : "View Materials Wright Off Report (wildflowers, microgreens)";
    const onClick = isDisabled ? undefined : (type === 'SB' ? openAllSBReport : type === 'Sets' ? openAllSetsReport : openAllMWReport);
    const iconColor = isDisabled ? 'lightgray' : 'gray';

    return (
      <div className="report-button-wrapper">
        <button 
          className={`report-icon-button ${isDisabled ? 'disabled' : ''}`} 
          onClick={onClick}
          title={title}
          style={{ color: iconColor }}
        >
          <FontAwesomeIcon icon={faFileAlt} />
        </button>
        {!isDisabled && noCount > 0 && (
          <span className="no-count-badge">{noCount}</span>
        )}
      </div>
    );
  };

  return (
    <div className="modal-popup-calendar-overlay">
      <div className="modal-popup-calendar">
        <div className="modal-popup-calendar-header">
          <div className="header-content">
            <h3>Date: {date?.toLocaleDateString()}</h3>
            {showSummary && (
              <div className="summary-data">
                <span>Total Planned: {summary.totalPlanned}</span>
                <span>Total Produced: {summary.totalProduced}</span>
                <span>Plan Completion: {summary.planCompletion}%</span>
              </div>
            )}
          </div>
          <button className="modal-popup-calendar-close-button" onClick={onClose}>×</button>
        </div>
        <div className="modal-popup-calendar-content">
          {loading && <p>Loading...</p>}
          {error && <p>Error: {error}</p>}
          {!loading && !error && data.length === 0 && <p>No data available for this date.</p>}
          {!loading && !error && data.length > 0 && hint && <p className="modal-popup-calendar-hint">{hint}</p>}
          {!loading && !error && data.length > 0 && (
            <div className="modal-popup-calendar-table-wrapper">
              <table className="modal-popup-calendar-table">
                <thead>
                  <tr>
                    {isHierarchical
                      ? mainColumns.map((col, index) => (
                          <th key={index}>
                            {col.label}
                            {col.key === 'expand' && (
                              <>
                                {isSets && getReportButton('Sets')}
                                {isMW && getReportButton('MW')}
                              </>
                            )}
                          </th>
                        ))
                      : columns.map((col, index) => (
                          <th key={index}>
                            {col.label}
                            {col.key === 'recID' && isSB && getReportButton('SB')}
                          </th>
                        ))}
                  </tr>
                </thead>
                <tbody>
                  {isHierarchical
                    ? data.map((item, index) => (
                        <React.Fragment key={index}>
                          <tr className="main-row">
                            <td>
                              <button onClick={() => toggleExpand(item.sku)}>
                                {expandedSKUs.has(item.sku) ? '△' : '▽'}
                              </button>
                            </td>
                            <td>{item.sku}</td>
                            <td>{item.name}</td>
                            <td>{item.employees}</td>
                            <td>{item.box_count}</td>
                            <td>{item.total_qty}</td>
                            <td>{item.planned_qty}</td>
                            <td>{item.plan_completion ? item.plan_completion.toFixed(2) + '%' : '0.00%'}</td>
                            {hasMaterial && (() => {
                              const off = isOff(Number(item.needed_lb), Number(item.used_lb)) ? 'cell-alert' : '';
                              const diff = Number(item.used_lb) - Number(item.needed_lb);
                              return (
                                <>
                                  <td>{lb(item.needed_lb)}</td>
                                  <td className={off}>{lb(item.used_lb)}</td>
                                  <td className={off}>{(diff > 0 ? '+' : '') + lb(diff)}</td>
                                </>
                              );
                            })()}
                          </tr>
                          {expandedSKUs.has(item.sku) && (
                            <tr className="detail-row">
                              <td colSpan={mainColumns.length}>
                                <table className="detail-table">
                                  <thead>
                                    <tr>
                                      {detailColumns.map((col, index) => <th key={index}>{col.label}</th>)}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {item.details.map((detail, detailIndex) => (
                                      <tr key={detailIndex}>
                                        {detailColumns.map((col, colIndex) => (
                                          <td key={colIndex}>
                                            {col.format ? col.format(detail[col.key]) : detail[col.key]}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    : data.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {columns.map((col, colIndex) => (
                            <td key={colIndex} className={col.cellClass ? col.cellClass(row) : undefined}>
                              {col.value
                                ? col.value(row)
                                : col.type === 'date' && row[col.key]
                                ? new Date(row[col.key]).toLocaleDateString()
                                : col.format && row[col.key] !== undefined && row[col.key] !== null
                                ? col.format(row[col.key])
                                : row[col.key] || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {allSBOpen && (
        <AllSBProductionMaterialsDailyReport
          isOpen={allSBOpen}
          onClose={() => setAllSBOpen(false)}
          date={date}
          products={data}
          allData={sbReportData}
        />
      )}
      {allSetsOpen && (
        <AllSetsProductionMaterialsDailyReport
          isOpen={allSetsOpen}
          onClose={() => setAllSetsOpen(false)}
          date={date}
          products={data}
          reportData={setsReportData}
          setNoCount={setSetsNoCount} // Pass setter to update noCount
        />
      )}
      {allMWOpen && (
        <AllMWProductionMaterialsDailyReport
          isOpen={allMWOpen}
          onClose={() => setAllMWOpen(false)}
          date={date}
          products={data}
          reportData={mwReportData}
          setNoCount={setMwNoCount} // Pass setter to update noCount
        />
      )}
    </div>
  );
}

export default PopupModal;